import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import { CircularProgress } from "@mui/material";
import { CheckCircle as CheckCircleIcon } from "@mui/icons-material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "utils/api";
import { toast } from "react-toastify";
import ChipInput from "./components/ChipInput";

function BenefitsManagement() {
  const [benefits, setBenefits] = useState([]);
  const [tutorBenefits, setTutorBenefits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Fetch benefits
  const fetchBenefits = async () => {
    setLoading(true);
    try {
      const response = await api.get("/planwithstudentreview");
      if (response.data.success) {
        setBenefits(response.data.data?.benefits || []);
        setTutorBenefits(response.data.data?.tutorBenefits || []);
      }
    } catch (error) {
      console.error("Error fetching benefits:", error);
      toast.error(error.response?.data?.message || "Failed to fetch benefits");
      setBenefits([]);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  useEffect(() => {
    fetchBenefits();
  }, []);

  // Handle add benefit
  const handleAddBenefit = (benefit) => {
    setBenefits((prev) => [...prev, benefit]);
  };

  // Handle delete benefit
  const handleDeleteBenefit = (index) => {
    setBenefits((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddTutorBenefit = (benefit) => {
    setTutorBenefits((prev) => [...prev, benefit]);
  };

  const handleDeleteTutorBenefit = (index) => {
    setTutorBenefits((prev) => prev.filter((_, i) => i !== index));
  };

  // Handle save
  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await api.put("/planwithstudentreview/benefits", {
        benefits: benefits,
        tutorBenefits: tutorBenefits,
      });
      if (response.data.success) {
        toast.success(response.data.message || "Benefits updated successfully!");
        fetchBenefits();
      }
    } catch (error) {
      console.error("Error updating benefits:", error);
      toast.error(error.response?.data?.message || "Failed to update benefits");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <ArgonTypography variant="h6">Plan Benefits</ArgonTypography>
                <ArgonButton
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </ArgonButton>
              </ArgonBox>

              <ArgonTypography variant="body2" color="text" mb={3}>
                Manage the benefits list for plans. Add or remove benefits as needed.
              </ArgonTypography>

              {loading ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    Fetching Benefits ..
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Please wait while we load the latest data.
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                <ArgonBox>
                  <ChipInput
                    label="Add Benefits"
                    items={benefits}
                    onAdd={handleAddBenefit}
                    onDelete={handleDeleteBenefit}
                    placeholder="Type a benefit and press Enter"
                  />

                  {benefits.length === 0 && (
                    <ArgonBox textAlign="center" py={3}>
                      <ArgonTypography variant="body2" color="text">
                        No benefits added yet. Start by adding your first benefit above.
                      </ArgonTypography>
                    </ArgonBox>
                  )}

                  <ArgonBox mt={4}>
                    <ArgonTypography variant="h6" mb={2}>
                      Tutor Benefits
                    </ArgonTypography>
                    <ChipInput
                      label="Add Tutor Benefits"
                      items={tutorBenefits}
                      onAdd={handleAddTutorBenefit}
                      onDelete={handleDeleteTutorBenefit}
                      placeholder="Type a tutor benefit and press Enter"
                    />

                    {tutorBenefits.length === 0 && (
                      <ArgonBox textAlign="center" py={3}>
                        <ArgonTypography variant="body2" color="text">
                          No tutor benefits added yet. Start by adding your first benefit above.
                        </ArgonTypography>
                      </ArgonBox>
                    )}
                  </ArgonBox>
                </ArgonBox>
              )}
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default BenefitsManagement;
